/**
 * React Query hooks for WL-2 (fieldiq-api#1338): jobs, units and rent-ins on a
 * job, day sheets, runs, the run setting and the field surface.
 *
 * Every key starts ['wirelineSlickline'] and every write invalidates that one
 * prefix (a run moves its sheet, the job's sheet list, the line state and the
 * field list at once). Every read spreads LOAD_FAILURE_QUERY_OPTIONS, so a
 * refusal reads as a refusal, never as an empty list.
 *
 * A WRITE RETURNS ITS WARNINGS. They are shown beside the result and never
 * stop anything; a refusal arrives as the mutation's error.
 *
 * THE FIELD SURFACE is snake_case on the wire and camelized once here
 * (wlCamelKeys), so the field screens read the office types.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { wlCamelKeys } from '../lib/wireline-slickline-jobs';
import type { WlMeta, WlResult } from '../types/wireline-slickline';
import type {
  WlFieldMySheets,
  WlJob,
  WlJobDetail,
  WlJobUnit,
  WlJobUnitWrite,
  WlJobWrite,
  WlLinkCandidates,
  WlRentIn,
  WlRentInWrite,
  WlRun,
  WlRunSettings,
  WlRunWrite,
  WlSheet,
  WlSheetLinks,
  WlSheetSummary,
  WlSheetWrite,
  WlWarningEntry,
  WlWriteResult,
} from '../types/wireline-slickline-jobs';
import { invalidateWl, unwrapWl, unwrapWlList, wlKeys } from './useWirelineSlickline';

const clean = (p?: Record<string, string | undefined>) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = v;
  return Object.keys(out).length ? out : undefined;
};

export const wlJobKeys = {
  jobs: (p?: Record<string, string | undefined>) => [...wlKeys.all, 'jobs', clean(p) ?? {}] as const,
  job: (id: string) => [...wlKeys.all, 'job', id] as const,
  jobSheets: (id: string) => [...wlKeys.all, 'jobSheets', id] as const,
  sheet: (id: string) => [...wlKeys.all, 'sheet', id] as const,
  candidates: (id: string) => [...wlKeys.all, 'candidates', id] as const,
  run: (id: string) => [...wlKeys.all, 'run', id] as const,
  runSettings: () => [...wlKeys.all, 'runSettings'] as const,
  fieldMine: () => [...wlKeys.all, 'field', 'mine'] as const,
  fieldSheet: (id: string) => [...wlKeys.all, 'field', 'sheet', id] as const,
};

const warningsOf = (body: unknown): WlWarningEntry[] => {
  const w = body && typeof body === 'object' ? (body as { warnings?: unknown }).warnings : undefined;
  return Array.isArray(w) ? (w as WlWarningEntry[]) : [];
};

/** A write's body as { data, warnings, meta }. */
function writeResult<T>(body: unknown): WlWriteResult<T> {
  const { data, meta } = unwrapWl<T>(body);
  return { data, warnings: warningsOf(body), meta };
}

/** A field body: camelize once, then the same unwrap. */
const fieldResult = <T>(body: unknown): WlWriteResult<T> => writeResult<T>(wlCamelKeys(body));

function useWlWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWl(qc) });
}

// ─── Jobs ────────────────────────────────────────────────────────────────────

export interface WlJobFilters {
  status?: string;
  customerId?: string;
  wellId?: string;
  unitId?: string;
  q?: string;
}

export function useWlJobs(filters?: WlJobFilters) {
  return useQuery({
    queryKey: wlJobKeys.jobs(filters as Record<string, string | undefined>),
    queryFn: async () => unwrapWlList<WlJob>((await api.wirelineSlickline.jobs.list(clean(filters as Record<string, string | undefined>))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlJob(id: string | undefined) {
  return useQuery({
    queryKey: wlJobKeys.job(id ?? ''),
    queryFn: async () => unwrapWl<WlJobDetail>((await api.wirelineSlickline.jobs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWlJob = () =>
  useWlWrite(async (body: WlJobWrite) => writeResult<WlJobDetail>((await api.wirelineSlickline.jobs.create(body)).data));
export const useUpdateWlJob = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlJobWrite }) =>
    writeResult<WlJobDetail>((await api.wirelineSlickline.jobs.update(id, body)).data));
export const useDeleteWlJob = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.jobs.remove(id)).data);
export const useSetWlPressureStack = () =>
  useWlWrite(async ({ jobId, unitId, equipmentId }: { jobId: string; unitId: string; equipmentId: string | null }) =>
    writeResult<WlJobDetail>((await api.wirelineSlickline.jobs.setPressureStack(jobId, { unit_id: unitId, equipment_id: equipmentId })).data));

export const useAddWlJobUnit = () =>
  useWlWrite(async ({ jobId, body }: { jobId: string; body: WlJobUnitWrite }) =>
    writeResult<WlJobUnit>((await api.wirelineSlickline.jobs.addUnit(jobId, body)).data));
export const useUpdateWlJobUnit = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlJobUnitWrite }) =>
    writeResult<WlJobUnit>((await api.wirelineSlickline.jobUnits.update(id, body)).data));
export const useRemoveWlJobUnit = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.jobUnits.remove(id)).data);

export const useAddWlRentIn = () =>
  useWlWrite(async ({ jobId, body }: { jobId: string; body: WlRentInWrite }) =>
    writeResult<WlRentIn>((await api.wirelineSlickline.jobs.addRentIn(jobId, body)).data));
export const useUpdateWlRentIn = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlRentInWrite }) =>
    writeResult<WlRentIn>((await api.wirelineSlickline.rentIns.update(id, body)).data));
export const useRemoveWlRentIn = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.rentIns.remove(id)).data);

// ─── Sheets ──────────────────────────────────────────────────────────────────

export function useWlJobSheets(jobId: string | undefined) {
  return useQuery({
    queryKey: wlJobKeys.jobSheets(jobId ?? ''),
    queryFn: async () => unwrapWlList<WlSheetSummary>((await api.wirelineSlickline.jobs.sheets(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlSheet(id: string | undefined) {
  return useQuery({
    queryKey: wlJobKeys.sheet(id ?? ''),
    queryFn: async () => unwrapWl<WlSheet>((await api.wirelineSlickline.sheets.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlLinkCandidates(sheetId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wlJobKeys.candidates(sheetId ?? ''),
    queryFn: async () => unwrapWl<WlLinkCandidates>((await api.wirelineSlickline.sheets.linkCandidates(sheetId as string)).data),
    enabled: !!sheetId && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useOpenWlSheet = () =>
  useWlWrite(async ({ jobId, body }: { jobId: string; body: WlSheetWrite }) =>
    writeResult<WlSheet>((await api.wirelineSlickline.jobs.openSheet(jobId, body)).data));
export const useSaveWlSheet = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlSheetWrite }) =>
    writeResult<WlSheet>((await api.wirelineSlickline.sheets.update(id, body)).data));
export const useSubmitWlSheet = () =>
  useWlWrite(async (id: string) => writeResult<WlSheet>((await api.wirelineSlickline.sheets.submit(id)).data));
export const useApproveWlSheet = () =>
  useWlWrite(async (id: string) => writeResult<WlSheet>((await api.wirelineSlickline.sheets.approve(id)).data));
export const useReturnWlSheet = () =>
  useWlWrite(async ({ id, reason }: { id: string; reason: string }) =>
    writeResult<WlSheet>((await api.wirelineSlickline.sheets.returnToCrew(id, { reason })).data));
export const useDeleteWlSheet = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.sheets.remove(id)).data);
export const useSetWlSheetLinks = () =>
  useWlWrite(async ({ id, kind, ids }: { id: string; kind: 'test' | 'drill'; ids: string[] }) =>
    writeResult<WlSheetLinks>(
      (kind === 'test'
        ? await api.wirelineSlickline.sheets.setPressureTests(id, { pressure_test_ids: ids })
        : await api.wirelineSlickline.sheets.setDrills(id, { drill_ids: ids })
      ).data,
    ));

// ─── Runs ────────────────────────────────────────────────────────────────────

export function useWlRun(id: string | undefined) {
  return useQuery({
    queryKey: wlJobKeys.run(id ?? ''),
    queryFn: async () => unwrapWl<WlRun>((await api.wirelineSlickline.runs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useAddWlRun = () =>
  useWlWrite(async ({ sheetId, body }: { sheetId: string; body: WlRunWrite }) =>
    writeResult<WlRun>((await api.wirelineSlickline.sheets.addRun(sheetId, body)).data));
export const useUpdateWlRun = () =>
  useWlWrite(async ({ id, body }: { id: string; body: Partial<WlRunWrite> }) =>
    writeResult<WlRun>((await api.wirelineSlickline.runs.update(id, body)).data));
export const useDeleteWlRun = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.runs.remove(id)).data);

// ─── The run setting ─────────────────────────────────────────────────────────

export function useWlRunSettings() {
  return useQuery({
    queryKey: wlJobKeys.runSettings(),
    queryFn: async () => unwrapWl<WlRunSettings>((await api.wirelineSlickline.runSettings.get()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useUpdateWlRunSettings = () =>
  useWlWrite(async (rigUpTestRequired: boolean) =>
    unwrapWl<WlRunSettings>((await api.wirelineSlickline.runSettings.update({ rig_up_test_required: rigUpTestRequired })).data));

// ─── Field ───────────────────────────────────────────────────────────────────

export function useWlFieldMySheets() {
  return useQuery({
    queryKey: wlJobKeys.fieldMine(),
    queryFn: async (): Promise<WlResult<WlFieldMySheets>> => {
      const { data, meta } = unwrapWl<WlFieldMySheets>(wlCamelKeys((await api.wirelineSlickline.field.mySheets()).data));
      return { data: { jobs: data?.jobs ?? [], sheets: data?.sheets ?? [] }, meta: meta as WlMeta };
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlFieldSheet(id: string | undefined) {
  return useQuery({
    queryKey: wlJobKeys.fieldSheet(id ?? ''),
    queryFn: async () => unwrapWl<WlSheet>(wlCamelKeys((await api.wirelineSlickline.field.sheet(id as string)).data)),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useSaveWlFieldSheet = () =>
  useWlWrite(async ({ jobId, workDate, body }: { jobId: string; workDate: string; body: WlSheetWrite }) =>
    fieldResult<WlSheet>((await api.wirelineSlickline.field.saveSheet(jobId, workDate, body)).data));
export const useSubmitWlFieldSheet = () =>
  useWlWrite(async (id: string) => fieldResult<WlSheet>((await api.wirelineSlickline.field.submit(id)).data));
export const useAddWlFieldRun = () =>
  useWlWrite(async ({ sheetId, body }: { sheetId: string; body: WlRunWrite }) =>
    fieldResult<WlRun>((await api.wirelineSlickline.field.addRun(sheetId, body)).data));
export const useUpdateWlFieldRun = () =>
  useWlWrite(async ({ id, body }: { id: string; body: Partial<WlRunWrite> }) =>
    fieldResult<WlRun>((await api.wirelineSlickline.field.updateRun(id, body)).data));
export const useDeleteWlFieldRun = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.field.removeRun(id)).data);
export const useRecordWlFieldTest = () =>
  useWlWrite(async ({ sheetId, body }: { sheetId: string; body: object }) =>
    fieldResult<{ record: unknown }>((await api.wirelineSlickline.field.recordTest(sheetId, body)).data));
export const useRecordWlFieldDrill = () =>
  useWlWrite(async ({ sheetId, body }: { sheetId: string; body: object }) =>
    fieldResult<{ record: unknown }>((await api.wirelineSlickline.field.recordDrill(sheetId, body)).data));
export const useRemoveWlFieldRecord = () =>
  useWlWrite(async ({ sheetId, kind, recordId }: { sheetId: string; kind: 'test' | 'drill'; recordId: string }) =>
    (await api.wirelineSlickline.field.removeRecord(sheetId, kind, recordId)).data);

/** A job's units, for the field "which unit" picker on a multi-unit job. */
export function useWlJobUnits(jobId: string | undefined) {
  return useQuery({
    queryKey: [...wlKeys.all, 'jobUnits', jobId ?? ''] as const,
    queryFn: async () => unwrapWlList<WlJobUnit>((await api.wirelineSlickline.jobs.units(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
